import { FC, JSX } from 'react';
import { InputLabel, MenuItem, Select } from '@mui/material';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { timeframes } from '../chart/constants/tradng-form-defaults';
import { SubmitButton } from '../../foundation/form/buttons/submit-button';
import { FormField, FormFieldProps } from '../../foundation/form/form-field';
import { FormContainer } from '../../foundation/layout/form-container';
import { FieldWrapper } from '../../foundation/layout/field-wrapper';
import type { ExchangeId } from '@shared/types';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

export type FileDownloadFormState = {
  exchangeId: ExchangeId;
  symbol: string;
  timeframe: string;
  historicalDays: number;
  fromDate?: string;
  toDate?: string;
};

const fileDownloadFormDefaultState: FileDownloadFormState = {
  exchangeId: 'BYBIT',
  symbol: 'BTCUSDT',
  timeframe: '4h',
  historicalDays: 100
};

export type FileDownloadFormProps = {
  onSubmit: (state: FileDownloadFormState) => void;
};

export const DownloadFormField = (
  props: FormFieldProps<FileDownloadFormState>
): JSX.Element => {
  return <FormField<FileDownloadFormState> {...props} />;
};

export const FileDownloadForm: FC<FileDownloadFormProps> = ({ onSubmit }) => {
  const formApi = useForm<FileDownloadFormState>({
    defaultValues: fileDownloadFormDefaultState
  });

  const { control, handleSubmit, setValue, watch } = formApi;

  const exchangeId = watch('exchangeId');

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <FormProvider {...formApi}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormContainer>
            <DownloadFormField
              type="exchange"
              name="exchangeId"
              onChange={(_, newValue) => {
                setValue('exchangeId', newValue as ExchangeId);
              }}
            />
            {/* Symbol Input */}
            <DownloadFormField
              type="symbol"
              name="symbol"
              exchangeId={exchangeId}
            />
            {/* Timeframe Selector */}
            <FieldWrapper>
              <InputLabel id="timeframe-label" sx={{ m: 1 }}>
                Timeframe
              </InputLabel>
              <Controller
                name="timeframe"
                control={control}
                render={({ field }) => (
                  <Select {...field} label="Timeframe">
                    {timeframes.map((timeframe) => (
                      <MenuItem key={timeframe} value={timeframe}>
                        {timeframe}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FieldWrapper>

            {/* Historical Days */}
            <DownloadFormField
              type="numeric"
              name="historicalDays"
              label="Historical Days"
            />

            <DownloadFormField type="date" name="fromDate" label="From date" />
            <DownloadFormField type="date" name="toDate" label="To date" />

            <FieldWrapper>
              <SubmitButton />
            </FieldWrapper>
          </FormContainer>
        </form>
      </FormProvider>
    </LocalizationProvider>
  );
};
